/**
 * Privacy Policy Page
 */

import { Link } from 'react-router-dom'
import { Footer } from '@/components/layout/Footer'
import { SEO } from '@/components/seo/SEO'

export const Privacy = () => {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* SEO */}
      <SEO
        title="Privacy Policy"
        description="Learn how Paiseh! protects your privacy and handles your personal data. We are committed to keeping your wishlist and gift information secure."
        canonicalUrl="https://paiseh.sg/privacy"
        noIndex={false}
      />

      {/* Main Content */}
      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
        <p className="text-sm text-gray-600 mb-8">Last updated: December 26, 2024</p>

        <div className="prose prose-gray max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Introduction</h2>
            <p className="text-gray-700 mb-4">
              Welcome to paiseh.sg. We respect your privacy and are committed to protecting your personal data. This privacy policy explains how we collect, use, and safeguard your information when you use our wishlist and gift exchange platform.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Information We Collect</h2>
            <h3 className="text-xl font-medium text-gray-800 mb-3">Account Information</h3>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>Email address (when you sign in with Google)</li>
              <li>Display name and profile picture (from your Google account)</li>
              <li>Anonymous identifier (if you choose guest access)</li>
            </ul>

            <h3 className="text-xl font-medium text-gray-800 mb-3">Wishlist Data</h3>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>Wishlist titles, descriptions, and categories</li>
              <li>Items you add to your wishlists (titles, descriptions, URLs, prices)</li>
              <li>Sharing settings and share links</li>
            </ul>

            <h3 className="text-xl font-medium text-gray-800 mb-3">Reservation Data</h3>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>Items you reserve on other users' wishlists</li>
              <li>Note: Wishlist owners cannot see who reserved their items (privacy guarantee)</li>
            </ul>

            <h3 className="text-xl font-medium text-gray-800 mb-3">Secret Santa Data</h3>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>Event details (title, date, budget)</li>
              <li>Participant information (names, emails)</li>
              <li>Gift assignments (only visible to you)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">How We Use Your Information</h2>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>To provide and maintain our service</li>
              <li>To enable wishlist sharing and gift coordination</li>
              <li>To manage Secret Santa events and assignments</li>
              <li>To protect against fraud and abuse</li>
              <li>To improve our service and develop new features</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Sharing</h2>
            <p className="text-gray-700 mb-4">
              We do not sell your personal information. We share data only in the following limited circumstances:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li><strong>With other users:</strong> When you share a wishlist, recipients can see the items you've added</li>
              <li><strong>With service providers:</strong> We use Firebase (Google Cloud Platform) to host and secure your data</li>
              <li><strong>For legal reasons:</strong> If required by law or to protect our rights</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Privacy Guarantees</h2>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <p className="text-green-900 font-medium mb-2">🔒 Reservation Privacy</p>
              <p className="text-green-800 text-sm">
                When you reserve an item, the wishlist owner can see that the item is reserved but cannot see who reserved it. This prevents gift duplicates while maintaining surprise.
              </p>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <p className="text-purple-900 font-medium mb-2">🎁 Secret Santa Privacy</p>
              <p className="text-purple-800 text-sm">
                Your Secret Santa assignment is private. Only you can see who you're giving a gift to. Other participants cannot see your assignment.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Security</h2>
            <p className="text-gray-700 mb-4">
              We implement industry-standard security measures to protect your data:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>All data is encrypted in transit using HTTPS</li>
              <li>Data is stored on secure Google Cloud Platform servers</li>
              <li>Access is controlled through Firebase Authentication</li>
              <li>Regular security audits and updates</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Your Rights</h2>
            <p className="text-gray-700 mb-4">You have the right to:</p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>Access your personal data</li>
              <li>Correct inaccurate data</li>
              <li>Delete your account and data</li>
              <li>Export your data</li>
              <li>Withdraw consent for data processing</li>
            </ul>
            <p className="text-gray-700">
              To exercise these rights, contact us at{' '}
              <a href="mailto:liu.kwn@gmail.com?subject=[Paiseh.sg]%20Privacy%20Policy" className="text-blue-600 hover:text-blue-700">
                liu.kwn@gmail.com
              </a>
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Cookies</h2>
            <p className="text-gray-700 mb-4">
              We use essential cookies to maintain your login session. See our{' '}
              <Link to="/cookies" className="text-blue-600 hover:text-blue-700">
                Cookie Policy
              </Link>{' '}
              for details.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Changes to This Policy</h2>
            <p className="text-gray-700">
              We may update this privacy policy from time to time. We will notify you of significant changes by posting a notice on our website.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Us</h2>
            <p className="text-gray-700">
              If you have questions about this privacy policy, please contact us at{' '}
              <a href="mailto:liu.kwn@gmail.com?subject=[Paiseh.sg]%20Privacy%20Policy" className="text-blue-600 hover:text-blue-700">
                liu.kwn@gmail.com
              </a>
            </p>
          </section>
        </div>

        <div className="mt-8 pt-8 border-t border-gray-200">
          <Link to="/" className="text-blue-600 hover:text-blue-700 font-medium">
            ← Back to Home
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  )
}
